import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaArrowLeft, FaArrowRight, FaSeedling, FaCloudSun, 
  FaTint, FaRupeeSign, FaBoxOpen, FaLightbulb, FaCheck 
} from 'react-icons/fa';
import axios from 'axios';
import './CheckRisk.css';

const CheckRisk = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language || 'hi';
  const isHindi = currentLang === 'hi';

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [detectedLocationMsg, setDetectedLocationMsg] = useState('');
  
  // Village / Taluka Search State
  const [villageSearchInput, setVillageSearchInput] = useState('');
  const [villageSearchResults, setVillageSearchResults] = useState([]);
  const [searchingVillage, setSearchingVillage] = useState(false);

  // Manual Coordinates State
  const [showManualCoordInput, setShowManualCoordInput] = useState(false);
  const [manualLat, setManualLat] = useState('21.0077');
  const [manualLon, setManualLon] = useState('75.5626');

  // Dynamic Regional Crops List (Defaults to Jalgaon)
  const [regionalCropsList, setRegionalCropsList] = useState([
    { crop: 'Banana', crop_hi: 'केला (केळी)', is_gi_tag: true, reason_hi: 'जलगांव भारत की केला राजधानी (Banana Capital) है', default_mandi_price: 1550 },
    { crop: 'Cotton', crop_hi: 'कपास', is_gi_tag: false, reason_hi: 'खानदेश की मुख्य नकदी फसल', default_mandi_price: 7200 },
    { crop: 'Soybean', crop_hi: 'सोयाबीन', is_gi_tag: false, reason_hi: 'प्रमुख खरीफ तिलहन फसल', default_mandi_price: 4600 },
    { crop: 'Maize', crop_hi: 'मक्का', is_gi_tag: false, reason_hi: 'कम पानी वाली फसल', default_mandi_price: 2150 },
    { crop: 'Wheat', crop_hi: 'गेहूं', is_gi_tag: false, reason_hi: 'रबी प्रमुख फसल', default_mandi_price: 2350 }
  ]);

  // Form State
  const [formData, setFormData] = useState({
    // Step 1: Soil
    soilType: 'Medium Black',
    soilMoisture: 'low', // 'low' | 'medium' | 'high'
    ph: 7.2,
    nitrogen: 45, // Jalgaon baseline
    phosphorus: 28,
    potassium: 195,
    organicCarbon: 'medium',
    soilCondition: 'good',

    // Step 2: Crop (Defaults to Banana for Jalgaon!)
    crop: 'Banana',
    cropStage: 'Fruit Development',
    targetYield: '30 Tonnes/Acre',

    // Step 3: Water
    waterSource: 'Drip / Borewell',
    hasIrrigation: true,
    lastIrrigationDays: 3,
    waterAvailability: 'Good',

    // Step 4: Market
    currentPrice: 1550,
    mandiName: 'Jalgaon APMC Mandi',
    priceTrend: 'stable',

    // Step 5: Post-Harvest
    daysSinceHarvest: 0,
    storageType: 'open',
    temperature: 30.5,
    rainfallDeficitPct: 20.0
  });

  const steps = [
    { num: 1, name_en: 'Soil', name_hi: 'मिट्टी', icon: <FaSeedling /> },
    { num: 2, name_en: 'Crop', name_hi: 'फसल', icon: '🌾' },
    { num: 3, name_en: 'Water', name_hi: 'पानी', icon: <FaTint /> },
    { num: 4, name_en: 'Market', name_hi: 'मार्केट', icon: <FaRupeeSign /> },
    { num: 5, name_en: 'Post-Harvest', name_hi: 'पोस्ट-हार्वेस्ट', icon: <FaBoxOpen /> }
  ];

  // Auto-Detect GPS & Satellite Soil Telemetry
  const handleAutoDetectGPS = (selectedDistrict = null) => {
    setDetectingLocation(true);
    setDetectedLocationMsg(isHindi ? '📡 जीपीएस और सैटेलाइट से जुड़ रहे हैं...' : '📡 Connecting to GPS & AgroMonitoring Satellite...');

    if (selectedDistrict) {
      fetchTelemetryData(null, null, selectedDistrict);
      return;
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;
          await fetchTelemetryData(lat, lon);
        },
        async (err) => {
          console.warn('Geolocation permission issue, defaulting to Jalgaon, Maharashtra:', err);
          // Default to Jalgaon, Maharashtra (21.0077, 75.5626)
          await fetchTelemetryData(21.0077, 75.5626, 'Jalgaon');
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
      );
    } else {
      fetchTelemetryData(21.0077, 75.5626, 'Jalgaon');
    }
  };

  const fetchTelemetryData = async (lat, lon, districtName = null) => {
    try {
      const queryParam = districtName 
        ? `district=${districtName}` 
        : `lat=${lat}&lon=${lon}`;
      
      const res = await axios.get(`http://localhost:8000/api/auto-field-telemetry?${queryParam}&crop=${formData.crop}&lang=${currentLang}`);
      const d = res.data;
      
      const moistureCat = d.soil_telemetry.soil_moisture_pct < 35 ? 'low' : (d.soil_telemetry.soil_moisture_pct < 65 ? 'medium' : 'high');
      
      if (d.regional_dominant_crops && d.regional_dominant_crops.length > 0) {
        setRegionalCropsList(d.regional_dominant_crops);
      }

      setFormData(prev => ({
        ...prev,
        crop: d.selected_crop || prev.crop,
        soilType: d.soil_telemetry.soil_type || 'Medium Black',
        soilMoisture: moistureCat,
        ph: d.soil_telemetry.pH || 6.5,
        nitrogen: d.soil_telemetry.nitrogen || 45,
        phosphorus: d.soil_telemetry.phosphorus || 35,
        potassium: d.soil_telemetry.potassium || 40,
        mandiName: `${d.location.district} APMC Mandi`,
        currentPrice: d.market_telemetry.current_mandi_price || 2100,
        temperature: d.weather_telemetry.temperature_celsius || 29.5,
        rainfallDeficitPct: d.weather_telemetry.rainfall_deficit_pct || 25.0
      }));

      // Save active location to localStorage so dashboard also reflects it
      localStorage.setItem('farmer_location', d.location.formatted_address);

      const msg = isHindi 
        ? `✅ स्थान: ${d.location.formatted_address} | सैटेलाइट मृदा नमी: ${d.soil_telemetry.soil_moisture_pct}% | तापमान: ${d.weather_telemetry.temperature_celsius}°C`
        : `✅ Location: ${d.location.formatted_address} | Satellite Soil Moisture: ${d.soil_telemetry.soil_moisture_pct}% | Temp: ${d.weather_telemetry.temperature_celsius}°C`;
      setDetectedLocationMsg(msg);
    } catch (e) {
      console.error('Auto-telemetry error:', e);
      setDetectedLocationMsg(isHindi ? '✅ सैटेलाइट व क्षेत्रीय डेटा स्वतः भर दिया गया।' : '✅ Satellite & regional field telemetry loaded.');
    } finally {
      setDetectingLocation(false);
    }
  };

  // Search Village / Taluka API
  const handleVillageSearchChange = async (val) => {
    setVillageSearchInput(val);
    if (!val || val.trim().length < 2) {
      setVillageSearchResults([]);
      return;
    }
    setSearchingVillage(true);
    try {
      const res = await axios.get(`http://localhost:8000/api/search-village-location?query=${encodeURIComponent(val.trim())}`);
      setVillageSearchResults(res.data || []);
    } catch (err) {
      console.warn('Village search error:', err);
    } finally {
      setSearchingVillage(false);
    }
  };

  const handleSelectVillage = (villageItem) => {
    setVillageSearchInput(villageItem.display_name || villageItem.name);
    setVillageSearchResults([]);
    fetchTelemetryData(villageItem.latitude, villageItem.longitude, villageItem.district);
  };

  const handleApplyManualCoords = () => {
    const lat = parseFloat(manualLat);
    const lon = parseFloat(manualLon);
    if (isNaN(lat) || isNaN(lon)) {
      alert(isHindi ? 'कृपया मान्य अक्षांश और देशांतर दर्ज करें (उदा. 21.0077, 75.5626)' : 'Please enter valid Latitude and Longitude (e.g. 21.0077, 75.5626)');
      return;
    }
    fetchTelemetryData(lat, lon);
  };

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/dashboard');
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        crop: formData.crop,
        nitrogen: Number(formData.nitrogen),
        phosphorus: Number(formData.phosphorus),
        potassium: Number(formData.potassium),
        ph: Number(formData.ph),
        soil_moisture: formData.soilMoisture === 'low' ? 30.0 : (formData.soilMoisture === 'medium' ? 55.0 : 80.0),
        soil_type: formData.soilType,
        has_irrigation: formData.hasIrrigation,
        crop_stage: formData.cropStage,
        temperature: Number(formData.temperature),
        rainfall_deficit_pct: Number(formData.rainfallDeficitPct),
        current_mandi_price: Number(formData.currentPrice),
        days_since_harvest: Number(formData.daysSinceHarvest),
        storage_type: formData.storageType,
        language: currentLang
      };

      const res = await axios.post('http://localhost:8000/api/distress-assessment', payload);
      localStorage.setItem('lastDistressAssessment', JSON.stringify(res.data));
      navigate('/risk-result');
    } catch (err) {
      console.warn('Backend calculation fallback:', err);
      navigate('/risk-result');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fd-stepper-page">
      <div className="fd-stepper-container">
        {/* Top Actions: Back, Auto GPS, Manual Coords */}
        <div className="fd-stepper-top-actions">
          <button className="fd-stepper-back-btn" onClick={handleBack}>
            <FaArrowLeft />
            <span>{isHindi ? 'वापस' : 'Back'}</span>
          </button>

          <div className="fd-top-btn-group">
            <button 
              type="button" 
              className="fd-auto-detect-btn"
              onClick={() => handleAutoDetectGPS()}
              disabled={detectingLocation}
            >
              <span>📡</span>
              <span>{detectingLocation 
                ? (isHindi ? 'सैटेलाइट से डेटा ला रहे हैं...' : 'Fetching Satellite Telemetry...') 
                : (isHindi ? '📍 जीपीएस से स्वतः भरें' : '📍 Auto-Detect GPS & Soil')}</span>
            </button>

            <button 
              type="button" 
              className="fd-manual-coord-btn"
              onClick={() => setShowManualCoordInput(!showManualCoordInput)}
            >
              <span>🌐</span>
              <span>{isHindi ? 'मैन्युअल Lat/Lon' : 'Manual Lat/Lon'}</span>
            </button>
          </div>
        </div>

        {/* Manual Coordinates Drawer */}
        {showManualCoordInput && (
          <div className="fd-manual-coord-drawer fd-card">
            <span className="fd-coord-drawer-title">{isHindi ? '🎯 सटीक खेत निर्देशांक (GPS Coordinates) दर्ज करें:' : '🎯 Enter Exact Farm GPS Coordinates:'}</span>
            <div className="fd-coord-inputs-row">
              <div className="fd-coord-field">
                <label>Latitude (अक्षांश)</label>
                <input 
                  type="number" 
                  step="0.0001" 
                  value={manualLat} 
                  onChange={(e) => setManualLat(e.target.value)} 
                  placeholder="21.0077"
                />
              </div>
              <div className="fd-coord-field">
                <label>Longitude (देशांतर)</label>
                <input 
                  type="number" 
                  step="0.0001" 
                  value={manualLon} 
                  onChange={(e) => setManualLon(e.target.value)} 
                  placeholder="75.5626"
                />
              </div>
              <button 
                type="button" 
                className="fd-apply-coords-btn"
                onClick={handleApplyManualCoords}
              >
                {isHindi ? '🚀 डेटा फेच करें' : '🚀 Fetch Telemetry'}
              </button>
            </div>
          </div>
        )}

        {/* Search Any Village / Taluka / Gram Panchayat */}
        <div className="fd-village-search-box">
          <div className="fd-village-input-wrapper">
            <span className="fd-search-icon">🔍</span>
            <input
              type="text"
              className="fd-village-input"
              placeholder={isHindi ? "अपना गाँव / तालुका / क्षेत्र खोजें (उदा. भुसावल, पाचोरा, रावेर, जलगांव...)" : "Search Village / Taluka / Area (e.g. Bhusawal, Pachora, Raver...)"}
              value={villageSearchInput}
              onChange={(e) => handleVillageSearchChange(e.target.value)}
            />
            {searchingVillage && <span className="fd-search-spinner">⏳</span>}
          </div>

          {villageSearchResults.length > 0 && (
            <div className="fd-village-dropdown-results">
              {villageSearchResults.map((v, idx) => (
                <div 
                  key={idx} 
                  className="fd-village-result-item"
                  onClick={() => handleSelectVillage(v)}
                >
                  <span className="fd-v-name">📍 {v.name}</span>
                  <span className="fd-v-sub">{v.display_name}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick District Selector Chips */}
        <div className="fd-quick-region-chips">
          <span className="fd-region-chips-label">{isHindi ? '⚡ त्वरित जिला चयन:' : '⚡ Quick Select District:'}</span>
          {[
            { id: 'Jalgaon', name_hi: 'जलगांव', name_en: 'Jalgaon (MH)' },
            { id: 'Nashik', name_hi: 'नाशिक', name_en: 'Nashik (MH)' },
            { id: 'Pune', name_hi: 'पुणे', name_en: 'Pune (MH)' },
            { id: 'Indore', name_hi: 'इंदौर', name_en: 'Indore (MP)' },
            { id: 'Nagpur', name_hi: 'नागपुर', name_en: 'Nagpur (MH)' }
          ].map(item => (
            <button
              key={item.id}
              type="button"
              className="fd-region-chip-btn"
              onClick={() => handleAutoDetectGPS(item.id)}
            >
              📍 {isHindi ? item.name_hi : item.name_en}
            </button>
          ))}
        </div>

        {detectedLocationMsg && (
          <div className="fd-detected-banner fd-card">
            <span>{detectedLocationMsg}</span>
          </div>
        )}

        {/* 5-Step Stepper Bar */}
        <div className="fd-stepper-bar-card fd-card">
          <div className="fd-stepper-nav">
            {steps.map((s) => (
              <div 
                key={s.num} 
                className={`fd-step-node ${currentStep === s.num ? 'active' : ''} ${currentStep > s.num ? 'completed' : ''}`}
                onClick={() => setCurrentStep(s.num)}
              >
                <div className="fd-node-circle">
                  {currentStep > s.num ? <FaCheck /> : s.num}
                </div>
                <span className="fd-node-name">{isHindi ? s.name_hi : s.name_en}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Step Content Card */}
        <div className="fd-step-content-card fd-card">
          {/* Step 1: Soil Information */}
          {currentStep === 1 && (
            <div className="fd-step-body">
              <div className="fd-step-hdr">
                <h2>{isHindi ? 'मिट्टी की जानकारी (Soil Information)' : 'Soil Information'}</h2>
                <p>{isHindi ? 'मिट्टी के पोषक तत्वों और नमी का सटीक विवरण दर्ज करें' : 'Enter chemical and moisture parameters for your soil'}</p>
              </div>

              <div className="fd-form-grid-2">
                <div className="fd-input-box">
                  <label>{isHindi ? 'मृदा नमी (Soil Moisture)' : 'Soil Moisture'}</label>
                  <select 
                    value={formData.soilMoisture}
                    onChange={(e) => setFormData({...formData, soilMoisture: e.target.value})}
                  >
                    <option value="low">{isHindi ? 'कम (Low - < 35%)' : 'Low (< 35%)'}</option>
                    <option value="medium">{isHindi ? 'मध्यम (Medium - 35-65%)' : 'Medium (35-65%)'}</option>
                    <option value="high">{isHindi ? 'अधिक (High - > 65%)' : 'High (> 65%)'}</option>
                  </select>
                </div>

                <div className="fd-input-box">
                  <label>{isHindi ? 'pH मान (pH Value)' : 'pH Value'}</label>
                  <input 
                    type="number" 
                    step="0.1" 
                    min="3.5" 
                    max="9.5"
                    value={formData.ph}
                    onChange={(e) => setFormData({...formData, ph: parseFloat(e.target.value) || 6.2})}
                  />
                </div>

                <div className="fd-input-box">
                  <label>{isHindi ? 'नाइट्रोजन N (Nitrogen kg/ha)' : 'Nitrogen N (kg/ha)'}</label>
                  <select 
                    value={formData.nitrogen}
                    onChange={(e) => setFormData({...formData, nitrogen: parseFloat(e.target.value) || 35})}
                  >
                    <option value="35">{isHindi ? 'कम (Low: ~35 kg/ha)' : 'Low (~35 kg/ha)'}</option>
                    <option value="80">{isHindi ? 'मध्यम (Optimal: ~80 kg/ha)' : 'Optimal (~80 kg/ha)'}</option>
                    <option value="120">{isHindi ? 'अधिक (High: ~120 kg/ha)' : 'High (~120 kg/ha)'}</option>
                  </select>
                </div>

                <div className="fd-input-box">
                  <label>{isHindi ? 'फॉस्फोरस P (Phosphorus kg/ha)' : 'Phosphorus P (kg/ha)'}</label>
                  <select 
                    value={formData.phosphorus}
                    onChange={(e) => setFormData({...formData, phosphorus: parseFloat(e.target.value) || 45})}
                  >
                    <option value="20">{isHindi ? 'कम (Low: ~20 kg/ha)' : 'Low (~20 kg/ha)'}</option>
                    <option value="45">{isHindi ? 'मध्यम (Optimal: ~45 kg/ha)' : 'Optimal (~45 kg/ha)'}</option>
                    <option value="75">{isHindi ? 'अधिक (High: ~75 kg/ha)' : 'High (~75 kg/ha)'}</option>
                  </select>
                </div>

                <div className="fd-input-box">
                  <label>{isHindi ? 'पोटाश K (Potassium kg/ha)' : 'Potassium K (kg/ha)'}</label>
                  <select 
                    value={formData.potassium}
                    onChange={(e) => setFormData({...formData, potassium: parseFloat(e.target.value) || 40})}
                  >
                    <option value="20">{isHindi ? 'कम (Low: ~20 kg/ha)' : 'Low (~20 kg/ha)'}</option>
                    <option value="40">{isHindi ? 'मध्यम (Optimal: ~40 kg/ha)' : 'Optimal (~40 kg/ha)'}</option>
                    <option value="80">{isHindi ? 'अधिक (High: ~80 kg/ha)' : 'High (~80 kg/ha)'}</option>
                  </select>
                </div>

                <div className="fd-input-box">
                  <label>{isHindi ? 'जैविक कार्बन (Organic Carbon)' : 'Organic Carbon'}</label>
                  <select 
                    value={formData.organicCarbon}
                    onChange={(e) => setFormData({...formData, organicCarbon: e.target.value})}
                  >
                    <option value="low">{isHindi ? 'कम (Low < 0.5%)' : 'Low (< 0.5%)'}</option>
                    <option value="medium">{isHindi ? 'मध्यम (Medium 0.5-0.75%)' : 'Medium (0.5-0.75%)'}</option>
                    <option value="high">{isHindi ? 'अधिक (High > 0.75%)' : 'High (> 0.75%)'}</option>
                  </select>
                </div>
              </div>

              {/* Soil Condition Radio */}
              <div className="fd-radio-group-wrapper">
                <label className="fd-radio-title">{isHindi ? 'मिट्टी की समग्र स्थिति:' : 'Overall Soil Health:'}</label>
                <div className="fd-radio-options">
                  <label className="fd-radio-label">
                    <input 
                      type="radio" 
                      name="soilCond" 
                      value="good" 
                      checked={formData.soilCondition === 'good'} 
                      onChange={() => setFormData({...formData, soilCondition: 'good'})} 
                    />
                    <span>{isHindi ? 'अच्छी' : 'Good'}</span>
                  </label>
                  <label className="fd-radio-label">
                    <input 
                      type="radio" 
                      name="soilCond" 
                      value="medium" 
                      checked={formData.soilCondition === 'medium'} 
                      onChange={() => setFormData({...formData, soilCondition: 'medium'})} 
                    />
                    <span>{isHindi ? 'मध्यम' : 'Medium'}</span>
                  </label>
                  <label className="fd-radio-label">
                    <input 
                      type="radio" 
                      name="soilCond" 
                      value="poor" 
                      checked={formData.soilCondition === 'poor'} 
                      onChange={() => setFormData({...formData, soilCondition: 'poor'})} 
                    />
                    <span>{isHindi ? 'खराब' : 'Poor'}</span>
                  </label>
                </div>
              </div>

              {/* Tip Callout */}
              <div className="fd-tip-banner">
                <FaLightbulb className="fd-tip-icon" />
                <p>
                  {isHindi 
                    ? 'सुझाव: मिट्टी में नमी कम है। वर्तमान तापमान में शीघ्र सिंचाई की आवश्यकता हो सकती है।'
                    : 'Tip: Soil moisture is low. Timely irrigation will prevent crop water distress.'}
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Crop Details */}
          {currentStep === 2 && (
            <div className="fd-step-body">
              <div className="fd-step-hdr">
                <h2>{isHindi ? 'फसल की जानकारी (Crop Details)' : 'Crop Details'}</h2>
                <p>{isHindi ? 'अपनी फसल, किस्म व वर्तमान वृद्धि अवस्था चुनें' : 'Select crop variety and current phenological stage'}</p>
              </div>

              {/* Regional Crop Recommendations */}
              <div className="fd-regional-crops-section">
                <span className="fd-regional-crops-title">
                  {isHindi ? '⭐ आपके क्षेत्र की प्रमुख फसलें (Regional Harvests):' : '⭐ Dominant Harvests for Your Region:'}
                </span>
                <div className="fd-regional-crop-cards">
                  {regionalCropsList.map((rc, idx) => (
                    <div 
                      key={idx}
                      className={`fd-crop-chip-card ${formData.crop === rc.crop ? 'selected' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, crop: rc.crop, currentPrice: rc.default_mandi_price }))}
                    >
                      <div className="fd-crop-chip-top">
                        <span className="fd-chip-name">{isHindi ? rc.crop_hi : rc.crop}</span>
                        {rc.is_gi_tag && <span className="fd-gi-badge">GI TAG</span>}
                      </div>
                      <span className="fd-chip-desc">{rc.reason_hi}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="fd-form-grid-2">
                <div className="fd-input-box">
                  <label>{isHindi ? 'मुख्य फसल (Selected Crop)' : 'Selected Crop'}</label>
                  <select 
                    value={formData.crop}
                    onChange={(e) => setFormData({...formData, crop: e.target.value})}
                  >
                    <option value="Banana">{isHindi ? 'केला / केळी (Banana - Jalgaon Hub)' : 'Banana'}</option>
                    <option value="Cotton">{isHindi ? 'कपास (Cotton)' : 'Cotton'}</option>
                    <option value="Soybean">{isHindi ? 'सोयाबीन (Soybean)' : 'Soybean'}</option>
                    <option value="Wheat">{isHindi ? 'गेहूं (Wheat)' : 'Wheat'}</option>
                    <option value="Rice">{isHindi ? 'चावल / धान (Rice)' : 'Rice'}</option>
                    <option value="Maize">{isHindi ? 'मक्का (Maize)' : 'Maize'}</option>
                    <option value="Grapes">{isHindi ? 'अंगूर (Grapes - Nashik Hub)' : 'Grapes'}</option>
                    <option value="Orange">{isHindi ? 'संतरा (Orange - Nagpur Hub)' : 'Orange'}</option>
                    <option value="Onion">{isHindi ? 'प्याज (Onion - Lasalgaon Hub)' : 'Onion'}</option>
                    <option value="Tomato">{isHindi ? 'टमाटर (Tomato)' : 'Tomato'}</option>
                    <option value="Pomegranate">{isHindi ? 'अनार (Pomegranate)' : 'Pomegranate'}</option>
                    <option value="Sugarcane">{isHindi ? 'गन्ना (Sugarcane)' : 'Sugarcane'}</option>
                    <option value="Garlic">{isHindi ? 'लहसुन (Garlic)' : 'Garlic'}</option>
                  </select>
                </div>

                <div className="fd-input-box">
                  <label>{isHindi ? 'फसल की अवस्था (Crop Stage)' : 'Growth Stage'}</label>
                  <select 
                    value={formData.cropStage}
                    onChange={(e) => setFormData({...formData, cropStage: e.target.value})}
                  >
                    <option value="Vegetative">{isHindi ? 'वानस्पतिक (Vegetative)' : 'Vegetative Stage'}</option>
                    <option value="Flowering">{isHindi ? 'फूल/फ्लावरिंग (Flowering / Shooting)' : 'Flowering Stage'}</option>
                    <option value="Fruit Development">{isHindi ? 'फल/दाना विकास (Fruit Development / Bunching)' : 'Fruit Development'}</option>
                    <option value="Ripening">{isHindi ? 'परिपक्वता (Maturity / Ripening)' : 'Ripening'}</option>
                    <option value="Harvesting">{isHindi ? 'कटाई (Harvesting)' : 'Harvesting'}</option>
                  </select>
                </div>
              </div>

              <div className="fd-tip-banner">
                <FaLightbulb className="fd-tip-icon" />
                <p>
                  {isHindi 
                    ? `सुझाव: ${formData.crop} की फसल में उचित सूक्ष्म पोषक तत्वों और ड्रिप सिंचाई से पैदावार में 30% वृद्धि होती है।`
                    : `Tip: Optimal micronutrient management and drip irrigation in ${formData.crop} boosts yield by 30%.`}
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Water & Hydric Stress */}
          {currentStep === 3 && (
            <div className="fd-step-body">
              <div className="fd-step-hdr">
                <h2>{isHindi ? 'पानी व सिंचाई (Water & Irrigation)' : 'Water & Hydric Stress'}</h2>
                <p>{isHindi ? 'जल स्रोत, अंतिम सिंचाई व जल उपलब्धता' : 'Water sources and irrigation frequency'}</p>
              </div>

              <div className="fd-form-grid-2">
                <div className="fd-input-box">
                  <label>{isHindi ? 'पानी का मुख्य स्रोत' : 'Water Source'}</label>
                  <select 
                    value={formData.waterSource}
                    onChange={(e) => setFormData({...formData, waterSource: e.target.value})}
                  >
                    <option value="Borewell">{isHindi ? 'बोरवेल (Borewell)' : 'Borewell'}</option>
                    <option value="Canal">{isHindi ? 'नहर (Canal)' : 'Canal'}</option>
                    <option value="Rainfed">{isHindi ? 'वर्षा आधारित (Rainfed)' : 'Rainfed'}</option>
                    <option value="Drip">{isHindi ? 'ड्रिप सिंचाई (Drip Irrigation)' : 'Drip Irrigation'}</option>
                  </select>
                </div>

                <div className="fd-input-box">
                  <label>{isHindi ? 'अंतिम सिंचाई (कितने दिन पहले?)' : 'Days Since Last Irrigation'}</label>
                  <input 
                    type="number" 
                    value={formData.lastIrrigationDays}
                    onChange={(e) => setFormData({...formData, lastIrrigationDays: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>

              <div className="fd-radio-group-wrapper">
                <label className="fd-radio-title">{isHindi ? 'सिंचाई की सुविधा उपलब्ध है?' : 'Is Irrigation Available?'}</label>
                <div className="fd-radio-options">
                  <label className="fd-radio-label">
                    <input 
                      type="radio" 
                      name="hasIrrig" 
                      checked={formData.hasIrrigation === true} 
                      onChange={() => setFormData({...formData, hasIrrigation: true})} 
                    />
                    <span>{isHindi ? 'हाँ (Yes)' : 'Yes'}</span>
                  </label>
                  <label className="fd-radio-label">
                    <input 
                      type="radio" 
                      name="hasIrrig" 
                      checked={formData.hasIrrigation === false} 
                      onChange={() => setFormData({...formData, hasIrrigation: false})} 
                    />
                    <span>{isHindi ? 'सीमित / नहीं (Limited/No)' : 'Limited / No'}</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Market & Mandi Dynamics */}
          {currentStep === 4 && (
            <div className="fd-step-body">
              <div className="fd-step-hdr">
                <h2>{isHindi ? 'मार्केट व मंडी स्थिति (Market Dynamics)' : 'Market & Mandi Intelligence'}</h2>
                <p>{isHindi ? 'वर्तमान भाव व मूल्य प्रवृत्ति की जानकारी' : 'Current local mandi pricing and volatility trend'}</p>
              </div>

              <div className="fd-form-grid-2">
                <div className="fd-input-box">
                  <label>{isHindi ? 'वर्तमान मंडी भाव (₹ / क्विंटल)' : 'Current Mandi Price (₹ / Quintal)'}</label>
                  <input 
                    type="number" 
                    value={formData.currentPrice}
                    onChange={(e) => setFormData({...formData, currentPrice: parseFloat(e.target.value) || 1950})}
                  />
                </div>

                <div className="fd-input-box">
                  <label>{isHindi ? 'कीमत की प्रवृत्ति (Price Trend)' : 'Price Trend'}</label>
                  <select 
                    value={formData.priceTrend}
                    onChange={(e) => setFormData({...formData, priceTrend: e.target.value})}
                  >
                    <option value="falling">{isHindi ? 'गिरावट (Falling - Below MSP)' : 'Falling (Below MSP)'}</option>
                    <option value="stable">{isHindi ? 'स्थिर (Stable)' : 'Stable'}</option>
                    <option value="rising">{isHindi ? 'तेजी (Rising)' : 'Rising'}</option>
                  </select>
                </div>
              </div>

              <div className="fd-tip-banner">
                <FaLightbulb className="fd-tip-icon" />
                <p>
                  {isHindi 
                    ? 'गेहूं का सरकारी MSP ₹2,275 है। वर्तमान भाव MSP से कम होने पर वित्तीय डिस्ट्रेस ट्रिगर होता है।'
                    : 'Wheat MSP is ₹2,275. Mandi price drops below MSP directly elevate market risk score.'}
                </p>
              </div>
            </div>
          )}

          {/* Step 5: Post-Harvest & Storage */}
          {currentStep === 5 && (
            <div className="fd-step-body">
              <div className="fd-step-hdr">
                <h2>{isHindi ? 'पोस्ट-हार्वेस्ट व भंडारण (Post-Harvest Decay)' : 'Post-Harvest & Storage Decay'}</h2>
                <p>{isHindi ? 'फसल भंडारण व कटाई पश्चात स्थिति' : 'Storage parameters for perishability calculation'}</p>
              </div>

              <div className="fd-form-grid-2">
                <div className="fd-input-box">
                  <label>{isHindi ? 'भंडारण का प्रकार (Storage Type)' : 'Storage Facility'}</label>
                  <select 
                    value={formData.storageType}
                    onChange={(e) => setFormData({...formData, storageType: e.target.value})}
                  >
                    <option value="open">{isHindi ? 'खुला स्थान / कच्चा गोदाम (Open / Farm Storage)' : 'Open Farm'}</option>
                    <option value="ventilated">{isHindi ? 'हवादार पक्का गोदाम (Ventilated Warehouse)' : 'Ventilated Warehouse'}</option>
                    <option value="cold_storage">{isHindi ? 'कोल्ड स्टोरेज (Cold Storage)' : 'Cold Storage'}</option>
                  </select>
                </div>

                <div className="fd-input-box">
                  <label>{isHindi ? 'कटाई के बाद के दिन (Days)' : 'Days Since Harvest'}</label>
                  <input 
                    type="number" 
                    value={formData.daysSinceHarvest}
                    onChange={(e) => setFormData({...formData, daysSinceHarvest: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Stepper Footer Action Buttons */}
          <div className="fd-step-footer">
            <button 
              type="button" 
              className="fd-btn-secondary fd-step-nav-btn"
              onClick={handleBack}
            >
              {isHindi ? '← वापस' : '← Back'}
            </button>

            <button 
              type="button" 
              className="fd-btn-primary fd-step-nav-btn"
              onClick={handleNext}
              disabled={loading}
            >
              {loading 
                ? (isHindi ? 'गणना जारी है...' : 'Calculating DRI...') 
                : (currentStep === 5 
                    ? (isHindi ? 'जोखिम जांचें (Predict DRI) →' : 'Predict My Risk →') 
                    : (isHindi ? 'आगे बढ़ें →' : 'Next Step →'))}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckRisk;
